import React, { useState } from "react";
import { Button, Modal, Table, Badge } from "react-bootstrap";
import {
  deleteGameById,
  fetchGamesList,
  restoreGame,
  toggleGameStatus,
} from "../../services/axios/apiManager";
import { format } from "date-fns";
import Filters from "./filters";
import BSPagination from "../../Components/pagination";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";

const GamesList = ({
  games,
  genres,
  tagsData,
  setGames,
  languages,
  pagination,
  setPagination,
  granted,
  categories,
  subCategories,
}) => {
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [modalOptions, setModalOptions] = useState({});
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Page change handler
  const handlePageChange = (page) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", page);
    setSearchParams(params);
  };

  // Status toggle
  const handleStatusChange = (game) => {
    if (!granted?.some((g) => g.type === "toggle-status")) {
      return toast.error("No permission.", { duration: 3000 });
    }
    if (game?.deletedAt === null) {
      setModalOptions({
        header: "Change Status",
        body: `Are you sure you want to toggle the status of "${game.title}"?`,
        okay: "Change",
        method: changeStatus,
        data: { id: game.id, status: game.status === 1 ? 0 : 1 },
      });
      setShowOptionsModal(true);
    } else {
      toast.info("Cannot change status of a deleted record!", {
        duration: 2000,
      });
    }
  };

  const changeStatus = async (data) => {
    const res = await toggleGameStatus(data);
    if (res?.data?.success) {
      const page = searchParams.get("page") || 1;
      const resList = await fetchGamesList(page, pagination.pageSize || 20);
      if (resList?.data?.data) setGames(resList.data.data);
      toast.success("Game status has been updated.");
    }
    setShowOptionsModal(false);
  };

  // Delete
  const handleDeleteGame = (game) => {
    if (!granted?.some((g) => g.type === "delete")) return;
    setModalOptions({
      header: "Delete Game",
      body: `Are you sure you want to delete "${game.title}"?`,
      okay: "Delete",
      method: deleteGame,
      data: { id: game.id },
    });
    setShowOptionsModal(true);
  };

  const deleteGame = async (data) => {
    const res = await deleteGameById(data.id);
    if (res?.data?.success) {
      const page = searchParams.get("page") || 1;
      const resList = await fetchGamesList(page, pagination.pageSize || 20);
      if (resList?.data?.data) setGames(resList.data.data);
      toast.success("Game data has been deleted.");
    }
    setShowOptionsModal(false);
  };

  // Restore
  const handleRestoreGame = (game) => {
    if (!granted?.some((g) => g.type === "delete")) return;
    setModalOptions({
      header: "Restore Game",
      body: `Are you sure you want to restore "${game.title}"?`,
      okay: "Restore",
      method: restoreGameById,
      data: { id: game.id },
    });
    setShowOptionsModal(true);
  };

  const restoreGameById = async (data) => {
    const res = await restoreGame(data.id);
    if (res?.data?.success) {
      const page = searchParams.get("page") || 1;
      const resList = await fetchGamesList(page, pagination.pageSize || 20);
      if (resList?.data?.data) setGames(resList.data.data);
      toast.success("Game data has been restored.");
    }
    setShowOptionsModal(false);
  };

  // Navigation helpers
  const viewGame = (id) => navigate(`/ViewGame/${id}`);
  const editGame = (id) => navigate(`/editGame/${id}`);
  const addNewGame = () => navigate("/addGame");

  return (
    <div className="container-fluid py-4 text-light bg-dark rounded shadow-lg">
      <div className="row mb-4 align-items-center">
        <div className="col-lg-10">
          <Filters
            pagination={pagination}
            setPagination={setPagination}
            genres={genres}
            tagsData={tagsData}
            languages={languages}
            setGames={setGames}
            categories={categories}
            subCategories={subCategories}
          />
        </div>
        <div className="col-lg-2 text-end">
          {granted?.some((g) => g.type === "create") && (
            <Button
              variant="success"
              className="px-4 shadow-sm"
              onClick={addNewGame}
              title="Add new game"
            >
              <i className="fas fa-plus me-2"></i>
              Add New
            </Button>
          )}
        </div>
      </div>

      <div className="table-responsive rounded shadow-sm border border-secondary border-opacity-25">
        <Table striped hover variant="dark" className="mb-0">
          <thead className="bg-secondary bg-opacity-25 text-nowrap">
            <tr>
              <th>Title</th>
              <th>Published By</th>
              <th>Mobile</th>
              <th>Date</th>
              <th>Category</th>
              <th>Sub-Category</th>
              <th>Orientation</th>
              <th>Dimensions</th>
              <th>Language</th>
              <th className="text-center">Status</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {games.length > 0 ? (
              games.map((g) => (
                <tr key={g.id} className="align-middle text-nowrap">
                  <td className="fw-semibold">{g.title}</td>
                  <td>{g.published_by || "—"}</td>
                  <td>
                    <Badge bg={g.mob_web_compatible ? "success" : "secondary"} className="fw-normal">
                      {g.mob_web_compatible ? "Yes" : "No"}
                    </Badge>
                  </td>
                  <td>{g.published_date ? format(new Date(g.published_date), "P") : "—"}</td>
                  <td>{g.game_category?.name || "—"}</td>
                  <td>{g.game_sub_category?.name || "—"}</td>
                  <td className="text-capitalize">{g.orientation || "—"}</td>
                  <td>{g.dimensions || "—"}</td>
                  <td>{g.language || "—"}</td>
                  <td className="text-center">
                    <button
                      className={`btn btn-sm fw-bold border-0 ${g.status === 1 ? "text-success" : "text-danger"}`}
                      onClick={() => handleStatusChange(g)}
                      title="Toggle Status"
                    >
                      {g.status === 1 ? "Active" : "Inactive"}
                    </button>
                  </td>
                  <td className="text-center">
                    <div className="d-flex justify-content-center gap-1">
                      {g.deletedAt ? (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRestoreGame(g)}
                          title="Restore"
                        >
                          <i className="fa-solid fa-rotate-right"></i>
                        </Button>
                      ) : (
                        <>
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => viewGame(g.id)}
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </Button>
                        {granted?.some((p) => p.type === "update") && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => editGame(g.id)}
                            title="Edit"
                          >
                            <i className="fas fa-edit"></i>
                          </Button>
                        )}
                        {!g.is_associated &&
                          granted?.some((p) => p.type === "delete") &&
                          g.status === 0 && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteGame(g)}
                              title="Delete"
                            >
                              <i className="fas fa-trash-alt"></i>
                            </Button>
                          )}
                      </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="11" className="text-center py-5 text-secondary">
                  No games found.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <div className="d-flex align-items-center justify-content-between mt-4">
        <div className="text-secondary small fw-medium">
          Showing {games.length} of {pagination?.totalRecords || 0} records
          <span className="ms-3 text-muted">Rows per page: {pagination.pageSize || 0}</span>
        </div>
        <BSPagination
          currentPage={Number(pagination.currentPage) || 1}
          totalPages={
            Number(pagination.totalPages) ||
            Math.ceil((pagination.totalRecords || 0) / (pagination.pageSize || 10)) ||
            1
          }
          onPageChange={handlePageChange}
          siblingCount={2}
          showFirstLast={true}
        />
      </div>

      {/* Options Modal */}
      <Modal
        show={showOptionsModal}
        onHide={() => setShowOptionsModal(false)}
        centered
        contentClassName="bg-dark text-light border border-secondary border-opacity-50 shadow-lg"
      >
        <Modal.Header closeButton closeVariant="white" className="border-bottom border-secondary border-opacity-25 py-3">
          <Modal.Title className="fs-5 fw-bold">{modalOptions.header}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-4 px-4 fs-6">
          {modalOptions.body}
        </Modal.Body>
        <Modal.Footer className="border-top border-secondary border-opacity-25 py-3">
          <Button
            variant="outline-secondary"
            className="px-4"
            onClick={() => setShowOptionsModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant={modalOptions.okay === "Delete" ? "danger" : "primary"}
            className="px-4 fw-bold"
            onClick={() => modalOptions.method(modalOptions.data)}
          >
            {modalOptions.okay}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>
        {`
          .table-responsive::-webkit-scrollbar {
            height: 8px;
          }
          .table-responsive::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.1);
            border-radius: 10px;
          }
          .table-responsive::-webkit-scrollbar-thumb:hover {
            background: rgba(255, 255, 255, 0.2);
          }
          .btn-sm i {
            font-size: 0.85rem;
          }
          .status-btn:hover {
            opacity: 0.8;
          }
        `}
      </style>
    </div>
  );
};

export default GamesList;
